import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Card,
  Typography,
  Modal,
  Form,
  Row,
  Col,
  message,
  Descriptions,
  Badge,
  Spin,
  Switch,
  InputNumber,
  Tooltip,
  Avatar,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { 
  serviceService, 
  type CreateServiceCategoryRequest 
} from "../services/serviceService";
import type { ServiceCategory, Course } from "../types";

const { Title } = Typography;
const { TextArea } = Input;

interface GetServiceCategoriesParams {
  page?: number;
  limit?: number;
  isActive?: boolean;
  search?: string;
}

const ServicesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useState<GetServiceCategoriesParams>({
    page: 1,
    limit: 10,
  });
  const [modal, contextHolder] = Modal.useModal();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [categoryDetails, setCategoryDetails] = useState<{ category: ServiceCategory; courses: Course[] } | null>(null);
  const [form] = Form.useForm();

  // Query สำหรับดึงข้อมูลหมวดหมู่บริการ
  const { data: categoriesResponse, isLoading, refetch } = useQuery(
    ['serviceCategories', searchParams],
    () => serviceService.getServiceCategories(searchParams),
    {
      keepPreviousData: true,
      onError: (error: any) => {
        message.error('ບໍ່ສາມາດໂຫລດຂໍ້ມູນໝວດໝູ່ບໍລິການໄດ້');
        console.error('Error fetching service categories:', error);
      }
    }
  );

  // Query สำหรับดึงรายละเอียดหมวดหมู่
  const { isLoading: isDetailLoading } = useQuery(
    ['serviceCategory', selectedCategory?._id],
    () => selectedCategory ? serviceService.getServiceCategory(selectedCategory._id) : null,
    {
      enabled: !!selectedCategory && detailModalVisible,
      onSuccess: (data) => {
        if (data) {
          setCategoryDetails(data.data);
        }
      },
      onError: (error: any) => {
        message.error('ບໍ່ສາມາດໂຫລດຂໍ້ມູນລາຍລະອຽດໝວດໝູ່ບໍລິການໄດ້');
        console.error('Error fetching category details:', error);
      }
    }
  );

  // Mutation สำหรับสร้างหมวดหมู่ใหม่
  const createCategoryMutation = useMutation(
    (data: CreateServiceCategoryRequest) => serviceService.createServiceCategory(data),
    {
      onSuccess: () => {
        message.success('ເພີ່ມໝວດໝູ່ບໍລິການໃໝ່ສຳເລັດ');
        queryClient.invalidateQueries(['serviceCategories']);
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດເພີ່ມໝວດໝູ່ໄດ້');
      }
    }
  );

  // Mutation สำหรับແກ້ໄຂหมวดหมู่
  const updateCategoryMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateServiceCategoryRequest> }) => 
      serviceService.updateServiceCategory(id, data),
    {
      onSuccess: () => {
        message.success('ແກ້ໄຂໝວດໝູ່ສຳເລັດ');
        queryClient.invalidateQueries(['serviceCategories']);
        queryClient.invalidateQueries(['serviceCategory']);
        setIsModalVisible(false);
        setEditingCategory(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດແກ້ໄຂໄດ້');
      }
    }
  );

  // Mutation สำหรับลบหมวดหมู่
  const deleteCategoryMutation = useMutation(
    (id: string) => serviceService.deleteServiceCategory(id),
    {
      onSuccess: () => {
        message.success('ລຶບໝວດໝູ່ບໍລິການສຳເລັດ');
        queryClient.invalidateQueries(['serviceCategories']);
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດລຶບໄດ້');
      }
    }
  );

  const handleEdit = (category: ServiceCategory) => {
    setEditingCategory(category);
    form.setFieldsValue(category);
    setIsModalVisible(true);
  };

  const handleViewDetails = (category: ServiceCategory) => {
    setSelectedCategory(category);
    setDetailModalVisible(true);
    setCategoryDetails(null);
  };

  const handleSubmit = (values: any) => {
    const formData = {
      ...values,
      sortOrder: values.sortOrder || 0,
    };

    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory._id, data: formData });
    } else {
      createCategoryMutation.mutate(formData);
    }
  };

  const handleDelete = (category: ServiceCategory) => {
    modal.confirm({
      title: "ຢືນຢັນການລຶບ",
      content: (
        <div>
          <p>ຕ້ອງການລຶບໝວດໝູ່ "{category.name}" ຫຼື ບໍ່?</p>
          <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
            ໝາຍເຫດ: ການລຶບຈະປ່ຽນສະຖານະເປັນບໍ່ໃຊ້ງານ ຫາກມີຄອສໃນໝວດໝູ່ນີ້ຈະບໍ່ສາມາດລຶບໄດ້
          </p>
        </div>
      ),
      okText: "ລຶບ",
      okType: "danger",
      cancelText: "ຍົກເລິກ",
      onOk: () => {
        deleteCategoryMutation.mutate(category._id);
      },
    });
  };

  const handleSearch = (value: string) => {
    setSearchParams(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleActiveFilter = (checked: boolean) => {
    setSearchParams(prev => ({ 
      ...prev, 
      isActive: checked ? undefined : true, 
      page: 1 
    }));
  };

  const handleTableChange = (pagination: any) => {
    setSearchParams(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }));
  };

  const columns = [
    {
      title: "ລຳດັບ",
      dataIndex: "sortOrder",
      key: "sortOrder",
      width: 80,
      render: (sortOrder: number) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SortAscendingOutlined style={{ marginRight: 4, color: '#1890ff' }} />
          {sortOrder}
        </div>
      ),
      sorter: true,
    },
    {
      title: "ໄອຄອນ",
      dataIndex: "icon",
      key: "icon",
      width: 80,
      render: (icon: string, _: ServiceCategory) => (
        <Avatar
          src={icon}
          icon={<AppstoreOutlined />}
          style={{ backgroundColor: icon ? 'transparent' : '#1890ff' }}
        />
      ),
    },
    {
      title: "ຊື່ໝວດໝູ່",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: ServiceCategory) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
              {record.description.length > 50 
                ? `${record.description.substring(0, 50)}...` 
                : record.description
              }
            </div>
          )}
        </div>
      ),
    },
    {
      title: "ສະຖານະ",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (isActive: boolean) => (
        <Badge
          status={isActive ? "success" : "error"}
          text={isActive ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
        />
      ),
      filters: [
        { text: 'ໃຊ້ງານ', value: true },
        { text: 'ບໍ່ໃຊ້ງານ', value: false },
      ],
    },
    {
      title: "ວັນທີສ້າງ",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('th-TH'),
      sorter: true,
    },
    {
      title: "ການດຳເນີນການ",
      key: "actions",
      width: 200,
      render: (_: any, record: ServiceCategory) => (
        <Space>
          <Tooltip title="ລາຍລະອຽດ">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="ແກ້ໄຂ">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              loading={updateCategoryMutation.isLoading}
            />
          </Tooltip>
          <Tooltip title="ລຶບ">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              loading={deleteCategoryMutation.isLoading}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const categories = categoriesResponse?.data?.categories || [];
  const pagination = categoriesResponse?.data?.pagination;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <AppstoreOutlined style={{ marginRight: 8 }} />
          ຈັດການໝວດໝູ່ບໍລິການ
        </Title>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => refetch()}
          loading={isLoading}
        >
          ຮີເຟຣ
        </Button>
      </div>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={10}>
            <Input.Search
              placeholder="ຄົ້ນຫາໝວດໝູ່ບໍລິການ..."
              allowClear
              style={{ width: "100%" }}
              onSearch={handleSearch}
              enterButton
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>ສະແດງທັງໝົດ:</span>
              <Switch
                checked={!searchParams.isActive}
                onChange={handleActiveFilter}
                size="small"
              />
            </div>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
              style={{ width: "100%" }}
              loading={createCategoryMutation.isLoading}
            >
              ເພີ່ມໝວດໝູ່ໃໝ່
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={categories}
          rowKey="_id"
          loading={isLoading}
          onChange={handleTableChange}
          pagination={{
            current: pagination?.current || 1,
            pageSize: searchParams.limit || 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
          }}
        />
      </Card>
  {contextHolder}
      {/* Add/Edit Category Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <AppstoreOutlined style={{ marginRight: 8 }} />
            {editingCategory ? "ແກ້ໄຂໝວດໝູ່ບໍລິການ" : "ເພີ່ມໝວດໝູ່ບໍລິການໃໝ່"}
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCategory(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
        confirmLoading={createCategoryMutation.isLoading || updateCategoryMutation.isLoading}
      >
        <Spin spinning={createCategoryMutation.isLoading || updateCategoryMutation.isLoading}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="name"
                  label="ຊື່ໝວດໝູ່"
                  rules={[{ required: true, message: "ກະລຸນາປ້ອນຊື່ໝວດໝູ່" }]}
                >
                  <Input placeholder="ຊື່ໝວດໝູ່" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="sortOrder"
                  label="ລຳດັບການສະແດງ"
                  tooltip="ຕົວເລກທີ່ໃຊ້ໃນການລຽງລຳດັບ (ນ້ອຍໄປຫຼາຍ)"
                >
                  <InputNumber 
                    min={0} 
                    placeholder="0" 
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="ຄຳອະທິບາຍ">
              <TextArea 
                rows={3} 
                placeholder="ຄຳອະທິບາຍໝວດໝູ່ບໍລິການ (ບໍ່ບັງຄັບ)"
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item 
              name="icon" 
              label="URL ໄອຄອນ"
              tooltip="URL ຂອງຮູບພາບທີ່ຈະໃຊ້ເປິນໄອຄອນ"
            >
              <Input 
                placeholder="https://example.com/icon.png (ບໍ່ບັງຄັບ)" 
                type="url"
              />
            </Form.Item>

            {editingCategory && (
              <Form.Item 
                name="isActive" 
                label="ສະຖານະ"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="ໃຊ້ງານ" 
                  unCheckedChildren="ບໍ່ໃຊ້ງານ" 
                />
              </Form.Item>
            )}

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={createCategoryMutation.isLoading || updateCategoryMutation.isLoading}
                >
                  {editingCategory ? "ແກ້ໄຂ" : "ເພີ່ມ"}
                </Button>
                <Button
                  onClick={() => {
                    setIsModalVisible(false);
                    setEditingCategory(null);
                    form.resetFields();
                  }}
                  disabled={createCategoryMutation.isLoading || updateCategoryMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Category Detail Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <AppstoreOutlined style={{ marginRight: 8 }} />
            {`ລາຍລະອຽດໝວດໝູ່: ${selectedCategory?.name}`}
          </div>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedCategory(null);
          setCategoryDetails(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            ປິດ
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => {
              if (selectedCategory) {
                setDetailModalVisible(false);
                handleEdit(selectedCategory);
              }
            }}
          >
            ແກ້ໄຂ
          </Button>
        ]}
        width={700}
      >
        <Spin spinning={isDetailLoading}>
          {categoryDetails && (
            <div>
              <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
                <Descriptions.Item label="ຊື່ໝວດໝູ່" span={2}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {categoryDetails.category.icon && (
                      <Avatar src={categoryDetails.category.icon} size="small" />
                    )}
                    <strong>{categoryDetails.category.name}</strong>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="ຄຳອະທິບາຍ" span={2}>
                  {categoryDetails.category.description || <span style={{ color: '#999' }}>ບໍ່ມີຄຳອະທິບາຍ</span>}
                </Descriptions.Item>
                <Descriptions.Item label="ລຳດັບການສະແດງ">
                  {categoryDetails.category.sortOrder}
                </Descriptions.Item>
                <Descriptions.Item label="ສະຖານະ">
                  <Badge
                    status={categoryDetails.category.isActive ? "success" : "error"}
                    text={categoryDetails.category.isActive ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="ວັນທີສ້າງ">
                  {new Date(categoryDetails.category.createdAt).toLocaleDateString('th-TH')}
                </Descriptions.Item>
                <Descriptions.Item label="ອັບເດດລ່າສຸດ">
                  {new Date(categoryDetails.category.updatedAt).toLocaleDateString('th-TH')}
                </Descriptions.Item>
              </Descriptions>

              <div>
                <Title level={4} style={{ marginBottom: 16 }}>
                  ຄອສໃນໝວດໝູ່ນີ້ ({categoryDetails.courses.length} ລາຍການ)
                </Title>
                {categoryDetails.courses.length > 0 ? (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {categoryDetails.courses.map((course) => (
                      <Card 
                        key={course._id} 
                        size="small"
                        style={{ borderLeft: '4px solid #1890ff' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 500 }}>{course.name}</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              ໄລຍະເວລາ: {course.duration} ນາທີ
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 500, color: '#f56a00' }}>
                              {course.pricing && course.pricing.length > 0 ? (
                                <div>
                                  {course.pricing.map((option, index) => (
                                    <div key={index} style={{ fontSize: '12px' }}>
                                      {option.sessions} ຄັ້ງ: {new Intl.NumberFormat('lo-LA').format(option.price.LAK)} ₭
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: '#999' }}>ບໍ່ມີຂໍ້ມູນລາຄາ</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px', 
                    color: '#999',
                    backgroundColor: '#fafafa',
                    borderRadius: '6px'
                  }}>
                    <AppstoreOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                    <div>ຍັງບໍ່ມີຄອສໃນໝວດໝູ່ນີ້</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default ServicesPage;